/*
 * Arcadia Commerce – JavaScript del tema.
 * Sin dependencias externas. Todas las operaciones de carrito usan la Ajax API oficial
 * de Shopify (/cart/add.js, /cart/change.js, /cart.js) y la Section Rendering API.
 * Cada componente funciona también sin JavaScript (formularios y enlaces reales).
 */
(function () {
  'use strict';

  var theme = window.ArcadiaTheme || { routes: {}, strings: {} };
  var routes = theme.routes;
  var strings = theme.strings;

  /* ---------- Utilidades ---------- */

  function qs(selector, root) { return (root || document).querySelector(selector); }
  function qsa(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }

  function announce(message) {
    var region = qs('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 50);
  }

  function getFocusable(container) {
    return qsa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || response.statusText);
          error.data = data;
          error.status = response.status;
          throw error;
        }
        return data;
      });
    });
  }

  function cartUrl(path) {
    // routes.cart = "/cart" (o "/es/cart" con prefijo de idioma)
    return routes.cart + path;
  }

  /* Formato de moneda equivalente al filtro money de Shopify (shop.money_format). */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{amount}} €';

    function withDelimiters(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = fmt.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      default: value = withDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }

  /* ---------- Contador del carrito ---------- */

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    qsa('[data-cart-count-label]').forEach(function (el) {
      var template = el.getAttribute('data-template');
      if (!template) {
        // Guarda la plantilla traducida la primera vez sustituyendo el número actual.
        template = el.textContent.replace(/\d+/, '{{ count }}');
        el.setAttribute('data-template', template);
      }
      el.textContent = template.replace('{{ count }}', count);
    });
  }

  function refreshCartCount() {
    return fetchJSON(cartUrl('.js'), { headers: { Accept: 'application/json' } })
      .then(function (cart) { updateCartCount(cart.item_count); return cart; })
      .catch(function () { /* el contador se actualizará al recargar */ });
  }

  /* ---------- Menú móvil ---------- */

  function initMenuDrawer(root) {
    qsa('[data-menu-open]', root).forEach(function (button) {
      var drawer = document.getElementById(button.getAttribute('aria-controls'));
      if (!drawer || button.dataset.bound) return;
      button.dataset.bound = 'true';

      function onKeydown(event) {
        if (event.key === 'Escape') { close(); return; }
        if (event.key !== 'Tab') return;
        var focusable = getFocusable(drawer);
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }

      function open() {
        drawer.hidden = false;
        // Fuerza el reflujo para que la transición de entrada se aplique.
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', onKeydown);
        var focusable = getFocusable(drawer);
        if (focusable.length) focusable[0].focus();
      }

      function close() {
        drawer.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', onKeydown);
        drawer.hidden = true;
        button.focus();
      }

      button.addEventListener('click', open);
      qsa('[data-menu-close]', drawer).forEach(function (el) { el.addEventListener('click', close); });
      window.matchMedia('(min-width: 990px)').addEventListener('change', function (mq) {
        if (mq.matches && !drawer.hidden) close();
      });
    });
  }

  /* ---------- Submenús de escritorio (details/summary) ---------- */

  function initDropdowns(root) {
    var dropdowns = qsa('[data-dropdown]', root);
    dropdowns.forEach(function (details) {
      if (details.dataset.bound) return;
      details.dataset.bound = 'true';
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        dropdowns.forEach(function (other) { if (other !== details) other.open = false; });
      });
      details.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && details.open) {
          details.open = false;
          qs('summary', details).focus();
        }
      });
    });
    if (!document.documentElement.dataset.dropdownOutside) {
      document.documentElement.dataset.dropdownOutside = 'true';
      document.addEventListener('click', function (event) {
        qsa('[data-dropdown][open]').forEach(function (details) {
          if (!details.contains(event.target)) details.open = false;
        });
      });
    }
  }

  /* ---------- Selectores de cantidad ---------- */

  function initQuantity(root) {
    qsa('[data-quantity]', root).forEach(function (wrapper) {
      if (wrapper.dataset.bound) return;
      wrapper.dataset.bound = 'true';
      var input = qs('input', wrapper);
      qsa('[data-quantity-step]', wrapper).forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(input.getAttribute('step'), 10) || 1;
          var min = input.hasAttribute('min') ? parseInt(input.getAttribute('min'), 10) : 1;
          var max = input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
          var direction = parseInt(button.getAttribute('data-quantity-step'), 10);
          var value = (parseInt(input.value, 10) || 0) + direction * step;
          value = Math.min(Math.max(value, min), max);
          if (String(value) === input.value) return;
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------- Añadir al carrito (ficha y tarjetas) ---------- */

  function initProductForms(root) {
    qsa('form[data-product-form]', root).forEach(function (form) {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', function (event) {
        // Si el navegador no admite fetch, se usa el envío normal del formulario.
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();

        var button = qs('[data-add-button]', form);
        var label = qs('[data-add-label]', form);
        var errorEl = qs('[data-form-error]', form);
        var successEl = qs('[data-form-success]', form);
        var successText = qs('[data-form-success-text]', form);
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        var originalLabel = label ? label.textContent : '';
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
        if (label) label.textContent = strings.adding;
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        if (successEl) successEl.hidden = true;

        fetchJSON(routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
          .then(function (item) {
            var message = strings.added + (item && item.product_title ? ': ' + item.product_title : '');
            if (successEl) {
              if (successText) successText.textContent = message + '.';
              successEl.hidden = false;
            }
            announce(message);
            if (label) label.textContent = strings.added;
            window.setTimeout(function () { if (label) label.textContent = originalLabel; }, 2000);
            return refreshCartCount();
          })
          .catch(function (error) {
            // Shopify devuelve 422 con «description» (p. ej. sin existencias suficientes).
            var message = (error && error.data && (error.data.description || error.data.message)) || strings.addError;
            if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
            announce(message);
            if (label) label.textContent = originalLabel;
          })
          .finally(function () {
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-loading');
          });
      });
    });
  }
  
/* ---------- Comprar ahora: ficha de producto ---------- */

function initBuyNow(root) {
  qsa('[data-buy-now]', root).forEach(function (button) {
    if (button.dataset.buyNowBound === 'true') return;
button.dataset.buyNowBound = 'true';

// Restablece el estado inicial del botón.
button.disabled = false;
button.textContent = 'Comprar ahora';
delete button.dataset.processing;

    button.addEventListener('click', function () {
      var section = button.closest('[data-product]');
      var form = section && qs('form[data-product-form]', section);

      if (!form || button.disabled || button.dataset.processing === 'true') return;

      var errorEl = qs('[data-form-error]', form);
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      button.dataset.processing = 'true';
      button.disabled = true;
      var originalText = button.textContent;
      button.textContent = 'Procesando…';

      fetchJSON(routes.cartAdd + '.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      })
        .then(function () {
          window.location.assign(routes.checkout || '/checkout');
        })
        .catch(function (error) {
          var message =
            (error && error.data &&
              (error.data.description || error.data.message)) ||
            strings.addError ||
            'No se pudo iniciar la compra. Inténtalo de nuevo.';

          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          }

          announce(message);
          button.disabled = false;
          button.textContent = originalText;
          delete button.dataset.processing;
        });
    });
  });
}


  /* ---------- Ficha de producto: variantes y galería ---------- */

  function initProduct(root) {
    qsa('[data-product]', root).forEach(function (container) {
      if (container.dataset.bound) return;
      container.dataset.bound = 'true';

      var jsonEl = qs('[data-product-json]', container);
      var moneyEl = qs('[data-money-format]', container);
      var productData = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      var moneyFormat = moneyEl ? JSON.parse(moneyEl.textContent) : null;
      var picker = qs('[data-variant-picker]', container);
      var select = qs('[data-variant-select]', container);
      var selectWrapper = qs('[data-variant-select-wrapper]', container);

      initGallery(container);

      if (!productData || !picker || !select) return;

      // Con JavaScript activo, los botones de opción sustituyen al desplegable.
      if (selectWrapper) selectWrapper.classList.add('visually-hidden');

      function selectedOptions() {
        return qsa('fieldset', picker).map(function (fieldset) {
          var checked = qs('input:checked', fieldset);
          return checked ? checked.value : null;
        });
      }

      function findVariant(options) {
        return productData.variants.find(function (variant) {
          return variant.options.every(function (value, index) { return value === options[index]; });
        });
      }

      function updateAvailabilityLabels(options) {
        // Marca qué valores darían una variante agotada o inexistente con el resto de opciones.
        qsa('fieldset', picker).forEach(function (fieldset, index) {
          qsa('input', fieldset).forEach(function (input) {
            var candidate = options.slice();
            candidate[index] = input.value;
            var variant = findVariant(candidate);
            var unavailable = !variant || !variant.available;
            var labelEl = input.nextElementSibling;
            if (labelEl) {
              labelEl.classList.toggle('is-unavailable', unavailable);
              var hint = qs('[data-unavailable-label]', labelEl);
              if (hint) hint.hidden = !unavailable;
            }
          });
          var selected = qs('[data-selected-value]', fieldset);
          if (selected) selected.textContent = options[index] || '';
        });
      }

      function updateVariant() {
        var options = selectedOptions();
        var variant = findVariant(options);
        var button = qs('[data-add-button]', container);
        var label = qs('[data-add-label]', container);
        var availability = qs('[data-availability]', container);
        var priceCurrent = qs('[data-price-current]', container);
        var priceCompare = qs('[data-price-compare]', container);
        var priceWrapper = qs('[data-price]', container);

        updateAvailabilityLabels(options);

        if (!variant) {
          // Combinación inexistente: no se puede comprar.
          if (button) button.disabled = true;
          if (label) label.textContent = strings.unavailable;
          if (availability) availability.innerHTML = '<span class="dot dot--out"></span>' + strings.unavailable;
          return;
        }

        select.value = String(variant.id);
        if (button) button.disabled = !variant.available;
        if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
        if (availability) {
          availability.innerHTML = variant.available
            ? '<span class="dot dot--in"></span>' + strings.inStock
            : '<span class="dot dot--out"></span>' + strings.soldOut;
        }

        if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price, moneyFormat);
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (priceCompare) {
          priceCompare.textContent = onSale ? formatMoney(variant.compare_at_price, moneyFormat) : '';
          priceCompare.hidden = !onSale;
        }
        if (priceWrapper) priceWrapper.classList.toggle('price--on-sale', !!onSale);

        if (variant.featured_media) showMedia(container, variant.featured_media.id);

        if (window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      picker.addEventListener('change', updateVariant);
      updateAvailabilityLabels(selectedOptions());
    });
  }

  function showMedia(container, mediaId) {
    var items = qsa('[data-media-id]', container);
    var target = items.find(function (item) { return item.getAttribute('data-media-id') === String(mediaId); });
    if (!target) return;
    items.forEach(function (item) { item.classList.toggle('is-active', item === target); });
    qsa('[data-thumb]', container).forEach(function (thumb) {
      if (thumb.getAttribute('data-thumb') === String(mediaId)) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    var list = qs('[data-gallery-list]', container);
    if (list && window.matchMedia('(max-width: 989px)').matches) {
      list.scrollTo({ left: target.offsetLeft - list.offsetLeft, behavior: 'smooth' });
    }
  }

  function initGallery(container) {
    qsa('[data-thumb]', container).forEach(function (thumb) {
      thumb.addEventListener('click', function () { showMedia(container, thumb.getAttribute('data-thumb')); });
    });
  }

  /* ---------- Carrito (página) ---------- */

  function initCart(root) {
    qsa('[data-cart-section]', root).forEach(function (section) {
      if (section.dataset.bound) return;
      section.dataset.bound = 'true';
      var sectionId = section.getAttribute('data-section-id');
      var timer;

      function setBusy(busy) {
        section.classList.toggle('is-loading', busy);
        section.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      function changeLine(line, quantity) {
        setBusy(true);
        var errorEl = qs('[data-cart-error]', section);
        if (errorEl) errorEl.hidden = true;

        fetchJSON(routes.cartChange + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: quantity, sections: [sectionId], sections_url: window.location.pathname })
        })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            var html = cart.sections && cart.sections[sectionId];
            if (html) {
              var parsed = new DOMParser().parseFromString(html, 'text/html');
              var fresh = qs('[data-cart-section]', parsed);
              if (fresh) {
                section.innerHTML = fresh.innerHTML;
                delete section.dataset.bound;
                setBusy(false);
                initCart(section.parentNode);
                initQuantity(section);
                var focusTarget = qs('[data-line-quantity="' + line + '"]', section) || qs('h1', section);
                if (focusTarget) focusTarget.focus({ preventScroll: true });
                announce(strings.cartUpdated);
                return;
              }
            }
            window.location.reload();
          })
          .catch(function (error) {
            setBusy(false);
            var message = (error && error.data && (error.data.description || error.data.message)) || strings.cartError;
            if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
            announce(message);
          });
      }

      section.addEventListener('change', function (event) {
        var input = event.target.closest('[data-line-quantity]');
        if (!input) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          var quantity = Math.max(0, parseInt(input.value, 10) || 0);
          changeLine(parseInt(input.getAttribute('data-line-quantity'), 10), quantity);
        }, 300);
      });

      section.addEventListener('click', function (event) {
        var remove = event.target.closest('[data-line-remove]');
        if (!remove) return;
        event.preventDefault();
        changeLine(parseInt(remove.getAttribute('data-line-remove'), 10), 0);
      });
    });
  }

  /* ---------- Filtros y orden ---------- */

  function initFacets(root) {
    qsa('[data-facets-form]', root).forEach(function (form) {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('change', function (event) {
        // El precio se aplica con el botón para no recargar a cada pulsación.
        if (event.target.type === 'number') return;
        form.requestSubmit ? form.requestSubmit() : form.submit();
      });
      form.addEventListener('submit', function () {
        // Elimina parámetros vacíos para mantener URLs limpias.
        qsa('input[type="number"]', form).forEach(function (input) { if (input.value === '') input.disabled = true; });
      });
    });
  }

  /* ---------- Recomendaciones ---------- */

  function initRecommendations(root) {
    qsa('[data-recommendations]', root).forEach(function (container) {
      if (container.dataset.bound || container.children.length) return;
      container.dataset.bound = 'true';
      var url = container.getAttribute('data-url');
      if (!url) return;
      var load = function () {
        fetch(url)
          .then(function (response) { return response.text(); })
          .then(function (text) {
            var parsed = new DOMParser().parseFromString(text, 'text/html');
            var fresh = qs('[data-recommendations]', parsed);
            if (fresh && fresh.innerHTML.trim().length) {
              container.innerHTML = fresh.innerHTML;
              initProductForms(container);
            }
          })
          .catch(function () { /* sin recomendaciones */ });
      };
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { observer.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        observer.observe(container);
      } else {
        load();
      }
    });
  }

  /* ---------- Direcciones de cliente ---------- */

  function initAddresses(root) {
    qsa('[data-country-select]', root).forEach(function (select) {
      if (select.dataset.bound || !window.Shopify || !window.Shopify.CountryProvinceSelector) return;
      select.dataset.bound = 'true';
      new window.Shopify.CountryProvinceSelector(select.id, select.getAttribute('data-province-id'), {
        hideElement: select.getAttribute('data-province-container')
      });
    });
    qsa('form[data-confirm]', root).forEach(function (form) {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', function (event) {
        if (!window.confirm(form.getAttribute('data-confirm'))) event.preventDefault();
      });
    });
  }
  // Restablece el botón al volver a la ficha con Atrás/Adelante.
window.addEventListener('pageshow', function () {
  qsa('[data-buy-now][data-processing="true"]').forEach(function (button) {
    button.disabled = false;
    button.textContent = 'Comprar ahora';
    delete button.dataset.processing;
  });
});

  /* ---------- Arranque ---------- */

  function init(root) {
    initMenuDrawer(root);
    initDropdowns(root);
    initQuantity(root);
    initProductForms(root);
    initBuyNow(root);
    initProduct(root);
    initCart(root);
    initFacets(root);
    initRecommendations(root);
    initAddresses(root);
  }

  function start() {
    init(document);
    // shopify_common.js se carga con defer; se reintenta al terminar la carga.
    window.addEventListener('load', function () { initAddresses(document); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Editor de temas: reinicializa las secciones al añadirlas o modificarlas.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
